import React, { useState } from "react";
import { Button } from "../ui/button";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { WHATSAPP_URL } from "../../config";

export default function Plans() {
  const { t } = useLang();
  const [yearly, setYearly] = useState(false);
  const openClaim = () => window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");

  const displayPrice = (p) => {
    if (!yearly) return { price: p.price, cadence: p.cadence, note: p.note };
    const numeric = parseInt(p.price.replace(/[^0-9]/g, ""), 10) * 10;
    return {
      price: "\u0e3f" + numeric.toLocaleString(),
      cadence: "/yr",
      note: t.plans.saveBadge + " \u00b7 billed annually",
    };
  };

  return (
    <section id="plan" className="relative py-24 md:py-32 bg-[var(--aa-wine)] text-[var(--aa-cream)] overflow-hidden">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full border border-white/10"></div>
      <div className="pointer-events-none absolute -bottom-40 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full border border-white/5"></div>

      <div className="relative mx-auto max-w-6xl px-6 md:px-8">
        <div className="reveal text-center">
          <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-cream)]/70 font-semibold mb-4">
            {t.plans.eyebrow}
          </div>
          <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05]">
            {t.plans.h2a} <span className="italic">{t.plans.h2b}</span>
          </h2>
          <p className="mt-5 text-[15px] md:text-[17px] text-[var(--aa-cream)]/75 max-w-xl mx-auto">
            {t.plans.p}
          </p>

          <div className="reveal mt-8 inline-flex items-center gap-1 p-1 rounded-full border border-white/20 bg-white/5">
            <button
              onClick={() => setYearly(false)}
              className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                !yearly ? "bg-[var(--aa-cream)] text-[var(--aa-wine)]" : "text-[var(--aa-cream)]/80"
              }`}
            >
              {t.plans.monthly}
            </button>
            <button
              onClick={() => setYearly(true)}
              className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-colors flex items-center gap-1.5 ${
                yearly ? "bg-[var(--aa-cream)] text-[var(--aa-wine)]" : "text-[var(--aa-cream)]/80"
              }`}
            >
              {t.plans.yearly}
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--aa-gold)]/20 text-[var(--aa-gold)] font-semibold">
                {t.plans.saveBadge}
              </span>
            </button>
          </div>
        </div>

        <div className="reveal grid md:grid-cols-2 gap-5 md:gap-6 mt-14 max-w-4xl mx-auto">
          {t.plans.list.map((p) => {
            const price = displayPrice(p);
            const rec = p.recommended;
            return (
              <div
                key={p.name}
                className={`relative rounded-3xl p-8 md:p-10 border transition-all duration-300 ${
                  rec
                    ? "bg-[var(--aa-cream)] text-[var(--aa-ink)] border-transparent shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]"
                    : "bg-white/[0.04] border-white/15 backdrop-blur-sm"
                }`}
              >
                {rec && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--aa-wine)] text-[var(--aa-cream)] text-[11px] font-semibold tracking-wide">
                    <Sparkles className="w-3 h-3" /> {t.plans.recommended}
                  </div>
                )}
                <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${rec ? "text-[var(--aa-wine)]" : "text-[var(--aa-cream)]/70"}`}>
                  {p.name}
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-5xl md:text-6xl">{price.price}</span>
                  <span className={`text-sm ${rec ? "text-[var(--aa-ink-soft)]" : "text-[var(--aa-cream)]/70"}`}>
                    {price.cadence}
                  </span>
                </div>
                <div className={`text-xs mt-1 ${rec ? "text-[var(--aa-ink-soft)]" : "text-[var(--aa-cream)]/60"}`}>
                  {price.note}
                </div>

                <ul className="mt-8 space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-[14px]">
                      <span
                        className={`mt-0.5 inline-flex w-5 h-5 rounded-full items-center justify-center flex-shrink-0 ${
                          rec ? "bg-[var(--aa-wine)] text-[var(--aa-cream)]" : "bg-white/15 text-[var(--aa-cream)]"
                        }`}
                      >
                        <Check className="w-3 h-3" />
                      </span>
                      <span className={rec ? "text-[var(--aa-ink)]" : "text-[var(--aa-cream)]/85"}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={openClaim}
                  className={`w-full mt-8 rounded-full py-6 font-semibold text-[14px] group ${
                    rec
                      ? "bg-[var(--aa-wine)] hover:bg-[var(--aa-wine-deep)] text-[var(--aa-cream)]"
                      : "bg-[var(--aa-cream)] hover:bg-white text-[var(--aa-wine)]"
                  }`}
                >
                  {p.cta}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            );
          })}
        </div>

        {t.plans.compareLink && (
          <div className="text-center mt-10 text-[13px] text-[var(--aa-cream)]/70">
            <a href="#" className="underline underline-offset-4 hover:text-[var(--aa-cream)]">
              {t.plans.compareLink}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
